/**
 * Owner value cards: which cards a site gets, and how their numbers are read
 * from YOM reports. Pure so it can be tested and reused by the page.
 */
import type { BreakdownData, OverviewData } from './reportClient';

export type ValueCardKey = 'visitors' | 'leads' | 'contacts' | 'orders' | 'chats';

/** Plugin ids (backend plugin registry) that make a card relevant. */
export function cardsForPlugins(installed: ReadonlySet<string>): ValueCardKey[] {
  const cards: ValueCardKey[] = ['visitors'];
  if (installed.has('contact-form') || installed.has('chatbot') || installed.has('newsletter')) {
    cards.push('leads');
  }
  // tel:/wa.me/mailto taps are auto-tracked on every site.
  cards.push('contacts');
  if (installed.has('ecommerce')) cards.push('orders');
  if (installed.has('chatbot')) cards.push('chats');
  return cards;
}

export interface ValueNumbers {
  visitors: number;
  pageviews: number;
  leads: number;
  orders: number;
  revenue: number;
  contacts: number;
  calls: number;
  whatsapp: number;
  chats: number;
}

export interface ValueSnapshot {
  current: ValueNumbers;
  previous: ValueNumbers | null;
}

const n = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function countEvent(b: BreakdownData | null | undefined, ...names: string[]): number {
  if (!b?.rows) return 0;
  return b.rows.reduce((sum, r) => (r.key && names.includes(r.key) ? sum + n(r.count) : sum), 0);
}

export function channelCount(b: BreakdownData | null | undefined, ...keys: string[]): number {
  if (!b?.rows) return 0;
  return b.rows.reduce(
    (sum, r) => (r.key && keys.includes(r.key.toLowerCase()) ? sum + n(r.count) : sum),
    0
  );
}

export function buildNumbers(
  kpis: OverviewData['kpis'] | null | undefined,
  events: BreakdownData | null | undefined,
  channels?: BreakdownData | null
): ValueNumbers {
  const k = kpis ?? {};
  return {
    visitors: n(k.visitors),
    pageviews: n(k.pageviews),
    leads: n(k.leads),
    orders: n(k.purchases),
    revenue: n(k.revenue),
    contacts: countEvent(events, 'contact_click'),
    calls: channelCount(channels, 'call', 'phone', 'tel'),
    whatsapp: channelCount(channels, 'whatsapp'),
    chats: countEvent(events, 'chatbot_conversation'),
  };
}

/** Relative change, or null when there is nothing to compare against. */
export function change(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined || previous === 0) return null;
  return (current - previous) / previous;
}
